import React, { useState, useEffect } from "react";
import {
  Space,
  DatePicker,
  Button,
  Table,
  Typography,
  Spin
} from "antd";
import Icon, { EditOutlined, ReloadOutlined } from "@ant-design/icons";
import { audioMessage as message } from '../Common/Notifications/audioMessage';
import { TrainingAPI } from "../../api/TrainingAPI";
import dayjs from "dayjs";
import { useSelector, useDispatch } from "react-redux";
import { setTeamSessions } from "../../store/actions/team.actions";
import { AutocompleteTextarea } from "../Common/AutocompleteTextarea/AutocompleteTextarea";


export const SessionNaming = () => {
  const date_format = "YYYY-MM-DD";

  const defaultEndDate = dayjs();
  const defaultStartDate = dayjs().subtract(30, 'days');

  const [dateRange, setDateRange] = useState([defaultStartDate, defaultEndDate]);
  const [sessions, setSessions] = useState([]);
  const [originalSessions, setOriginalSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);

  const trainingAPI = new TrainingAPI();
  const dispatch = useDispatch();

  const { sessions: currentSessionsList } = useSelector(({ team }) => team);

  const { Paragraph } = Typography;

  const onDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  useEffect(() => {
    if (dateRange && dateRange[0] && dateRange[1] && 
        dayjs.isDayjs(dateRange[0]) && dayjs.isDayjs(dateRange[1]) &&
        dateRange[0].isValid() && dateRange[1].isValid()) {
      fetchSessionData(dateRange[0].format(date_format), dateRange[1].format(date_format));
    } else {
      setSessions([]);
      setOriginalSessions([]);
    }
  }, [dateRange, refresh]);

  const fetchSessionData = async (startDate, endDate) => {
    if (!startDate || !endDate) {
      message.error("Both start date and end date are required");
      setSessions([]);
      setOriginalSessions([]);
      return;
    }

    if (!dayjs(startDate, date_format, true).isValid() || !dayjs(endDate, date_format, true).isValid()) {
      message.error("Invalid date format. Please select valid dates");
      setSessions([]);
      setOriginalSessions([]);
      return;
    }

    if (dayjs(startDate).isAfter(dayjs(endDate))) {
      message.error("Start date must be before or equal to end date");
      setSessions([]);
      setOriginalSessions([]);
      return;
    }

    setLoading(true);
    try {
      const resp = await trainingAPI.getSessionNaming(startDate, endDate);
      
      const sessionsWithKeys = resp.map((session, index) => ({
        ...session,
        key: index,
        original_name: session.session_name
      }));
      
      setSessions(sessionsWithKeys);
      setOriginalSessions(sessionsWithKeys);
    } catch (err) {
      if (err.response?.status === 400) {
        message.error(err.response?.data || "Invalid date parameters provided");
      } else {
        message.error("Error fetching session data for the given date range");
      }
      setSessions([]);
      setOriginalSessions([]);
    }
    setLoading(false);
  };

  const handleSessionNameUpdate = async (date, oldSessionName, newSessionName) => {
    const update = [{
      date: date,
      old_session_name: oldSessionName,
      new_session_name: newSessionName
    }];

    const resp = await trainingAPI.updateSessionNaming(update);

    const updatedSessions = [...sessions];
    const updatedOriginalSessions = [...originalSessions];
    
    resp.forEach((updatedSession) => {
      const sessionIndex = updatedSessions.findIndex(s => s.date === updatedSession.date);
      if (sessionIndex !== -1) {
        updatedSessions[sessionIndex] = {
          ...updatedSession,
          key: sessionIndex,
          original_name: updatedSession.session_name
        };
        updatedOriginalSessions[sessionIndex] = {
          ...updatedSession,
          key: sessionIndex,
          original_name: updatedSession.session_name
        };
      }
    });
    
    setSessions(updatedSessions);
    setOriginalSessions(updatedOriginalSessions);

    // update redux store with new session name in the sessions list
    if (currentSessionsList && Array.isArray(currentSessionsList)) {
      const updatedSessionsList = [...currentSessionsList];

      const oldIndex = updatedSessionsList.indexOf(oldSessionName);
      if (oldIndex !== -1) {
        updatedSessionsList[oldIndex] = newSessionName;
      }

      if (!updatedSessionsList.includes(newSessionName)) {
        updatedSessionsList.push(newSessionName);
      }

      dispatch(setTeamSessions(updatedSessionsList));
    }
  };

  // Custom editable cell component for session names with autocomplete
  const EditableSessionNameCell = ({ text, record, index, onSave }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(text);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
      if (!isEditing) {
        setEditValue(text);
      }
    }, [text, isEditing]);

    const handleSave = async () => {
      if (editValue === text) {
        setIsEditing(false);
        return;
      }

      setSaving(true);
      try {
        await onSave(record.date, record.original_name, editValue);
        setIsEditing(false);
        setRefresh(!refresh);
        message.success("Session name updated successfully");
      } catch (err) {
        console.log(err);
        if (err.status === 400) {
          message.error(err.data || "Error updating session name");
        } else {
          message.error("Error updating session name");
        }
      } finally {
        setSaving(false);
      }
    };

    const handleCancel = () => {
      setEditValue(text);
      setIsEditing(false);
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleCancel();
      }
    };

    if (!isEditing) {
      return (
        <div
          onClick={() => {
            setIsEditing(true);
            setEditValue(text);
          }}
          style={{
            cursor: 'pointer',
            padding: '4px 11px',
            minHeight: '22px',
            borderRadius: '2px',
            transition: 'background-color 0.3s',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          title="Click to edit"
        >
          <span>{text}</span>
          <EditOutlined 
            style={{ 
              color: '#1890ff', 
              fontSize: '14px',
              flexShrink: 0
            }} 
          />
        </div>
      );
    }

    return (
      <div onKeyDown={handleKeyDown}>
        <AutocompleteTextarea
          value={editValue}
          onChange={setEditValue}
          entityTypes={['sessions']}
          maxSuggestions={10}
          minChars={1}
          rows={1}
          placeholder="Enter session name"
          theme="textarea"
          onEnter={handleSave}
          style={{ marginBottom: '8px' }}
        />
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button 
            size="small" 
            type="primary" 
            onClick={handleSave}
            loading={saving}
            disabled={saving}
          >
            Save
          </Button>
          <Button 
            size="small" 
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      sorter: {
        compare: (a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf(),
        multiple: 1,
      },
      defaultSortOrder: 'descend',
      render: (text) => dayjs(text).format(date_format)
    },
    {
      title: 'Session Name',
      dataIndex: 'session_name',
      key: 'session_name',
      render: (text, record, index) => (
        <EditableSessionNameCell 
          text={text} 
          record={record} 
          index={index} 
          onSave={handleSessionNameUpdate}
        />
      ),
    },
    {
      title: 'Num Players',
      dataIndex: 'num_players',
      key: 'num_players',
    },
    {
      title: 'Avg Duration',
      dataIndex: 'avg_duration',
      key: 'avg_duration',
      render: (text) => `${text} min`
    },
  ];

  return (
  <div>
      <Typography.Title level={4} style={{marginBottom: 0, marginTop: 0}}>Rename Sessions</Typography.Title>
      <hr style={{marginTop: 0}} />
      <Paragraph>Please select a date range to find your sessions:
          <DatePicker.RangePicker
            onChange={onDateRangeChange}
            value={dateRange}
            style={{ width: 300, borderRadius: '8px', marginLeft: "10px" }}
            format={date_format}
          />
          <Button icon={<ReloadOutlined />} style={{ marginLeft: '10px' }} onClick={() => setRefresh(!refresh)}/>
      </Paragraph>      
      <Typography.Text type="secondary" style={{ marginBottom: '12px', display: 'block' }}>
        Click on <EditOutlined style={{ color: '#1890ff', fontSize: '14px',flexShrink: 0}} /> to change the name of your training session.
      </Typography.Text>
      <Spin spinning={loading} tip="Loading sessions...">
        {dateRange && dateRange[0] && dateRange[1] && sessions.length === 0 && !loading && (
          <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
            No sessions for the given date range
          </div>
        )}
        
        {sessions.length > 0 && (
          <Table
            columns={columns}
            dataSource={sessions}
            size="small"
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "30"],
              position: ["topLeft"],
            }}
            scroll={{ y: 400 }}
            bordered
            style={{ marginBottom: '16px' }}
          />
        )}
      </Spin>
    </div>
  );
};

